import * as THREE from 'three';
import { resolveCircle } from '../../shared/collision.js';
import { hash2 } from '../../shared/rng.js';
import { surfaceLift } from '../../shared/geo.js';
import { SKIN_TONES, SHIRT_COLORS, TROUSER_COLORS, makeCharacter, animateCharacter } from './character.js';
import { VEHICLES, vehicleMesh, vehicleTypeOf } from './vehicles.js';
import { Characters } from './characters.js';
import { billboardMaterial, cardQuad } from '../world/billboard.js';

const NEAR = 42; // metres: townspeople this close get an animated body from the pool
const NEAR_OUT = 50; // and give it back beyond this, so nobody flickers at the edge

const DRESS = ['#c8402f', '#2f6fb4', '#f2b705', '#2f8a56', '#7a3fa0', '#e07a28', '#d6508a', '#3aa6a0'];
const MAX = 320; // townspeople drawn at once
const FAR = 70; // metres beyond which a walker's pose is refreshed every third frame
const HAIR = new THREE.Color('#17120e');
const SHOE = new THREE.Color('#1a1a1a');
const ZERO = new THREE.Matrix4().makeScale(0, 0, 0);
// the far ring as cards: eight outfits photographed from the real bodies once they have loaded
// (four men's shirts, four women's dresses); a person wears the card nearest its own colours
const CARD_MEN = ['#f4f1e8', '#2f6fb4', '#c8402f', '#f2b705'];
const CARD_WOMEN = ['#c8402f', '#2f6fb4', '#f2b705', '#2f8a56'];
const CARD_W = 1.0;
const CARD_H = 1.9;
const CARDS = CARD_MEN.length + CARD_WOMEN.length;
const FRAMES = 4; // atlas rows: standing, two walking frames with the legs the other way, seated
export const SEATED_FRAME = 3;
// school uniforms: blue or white shirts over navy, as the schools of the North West wear
const UNIFORM_TOPS = ['#2f6fb4', '#f4f1e8', '#2f6fb4'];
const UNIFORM_BOTTOMS = ['#2a3f6a', '#2a3f6a', '#b89b6a'];
const cardColours = [...CARD_MEN, ...CARD_WOMEN].map((h) => new THREE.Color(h));
function cardFor(sex, top) {
  const from = sex === 0 ? CARD_MEN.length : 0;
  const to = sex === 0 ? CARDS : CARD_MEN.length;
  let best = from;
  let bestD = Infinity;
  for (let k = from; k < to; k++) {
    const c = cardColours[k];
    const d = (c.r - top.r) ** 2 + (c.g - top.g) ** 2 + (c.b - top.b) ** 2;
    if (d < bestD) {
      bestD = d;
      best = k;
    }
  }
  return best;
}

// Every townsperson is the same eleven boxes, so all of them together cost eleven draw calls: one
// InstancedMesh per body part, coloured per instance (skin, shirt, wrapper, trousers). Parts that
// swing when walking hang from a pivot at their top end.
const PARTS = {
  head: { size: [0.25, 0.27, 0.25], at: [0, 1.6, 0] },
  hair: { size: [0.27, 0.09, 0.27], at: [0, 1.75, 0] },
  torso: { size: [0.44, 0.58, 0.24], at: [0, 1.13, 0] },
  hips: { size: [0.4, 0.14, 0.23], at: [0, 0.8, 0] },
  skirt: { size: [0.46, 0.5, 0.3], at: [0, 0.62, 0] }, // the wrapper or dress, to below the knee
  armL: { size: [0.11, 0.6, 0.12], at: [0.28, 1.1, 0], pivot: [0.28, 1.4, 0] },
  armR: { size: [0.11, 0.6, 0.12], at: [-0.28, 1.1, 0], pivot: [-0.28, 1.4, 0] },
  legL: { size: [0.16, 0.78, 0.17], at: [0.11, 0.39, 0], pivot: [0.11, 0.78, 0] },
  legR: { size: [0.16, 0.78, 0.17], at: [-0.11, 0.39, 0], pivot: [-0.11, 0.78, 0] },
  shoeL: { size: [0.17, 0.08, 0.27], at: [0.11, 0.04, 0.04], pivot: [0.11, 0.78, 0] },
  shoeR: { size: [0.17, 0.08, 0.27], at: [-0.11, 0.04, 0.04], pivot: [-0.11, 0.78, 0] },
};

function sizeFor(age) {
  if (age < 3) return 0.42;
  if (age < 7) return 0.56;
  if (age < 12) return 0.72;
  if (age < 16) return 0.88;
  return 0.96 + (age > 65 ? -0.04 : 0);
}

// The townspeople the server says are near, walking their daily rounds, and the other players.
export class People {
  // bodies: how many real skinned bodies the near ring may use (by quality)
  constructor(scene, heightmap, chunks, bodies = 24) {
    this.scene = scene;
    this.heightmap = heightmap;
    this.chunks = chunks;
    this.npcs = new Map();
    this.players = new Map();
    this.hits = [];
    this.material = new THREE.MeshLambertMaterial();
    this.parts = {};
    for (const [name, def] of Object.entries(PARTS)) {
      const g = new THREE.BoxGeometry(...def.size);
      if (def.pivot) g.translate(def.at[0] - def.pivot[0], def.at[1] - def.pivot[1], def.at[2] - def.pivot[2]);
      else g.translate(...def.at);
      const mesh = new THREE.InstancedMesh(g, this.material, MAX);
      // one invisible copy is always drawn so the shader compiles during loading, not mid-game
      mesh.count = 1;
      mesh.setMatrixAt(0, ZERO);
      mesh.castShadow = true;
      mesh.frustumCulled = false;
      mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
      // instance colours exist from the first frame, so the renderer compiles for them once
      mesh.setColorAt(0, HAIR);
      scene.add(mesh);
      this.parts[name] = mesh;
    }
    this.root = new THREE.Object3D();
    this.limb = new THREE.Object3D();
    this.root.add(this.limb);
    // the near ring: real skinned bodies for whoever is closest (loads in the background)
    this.characters = new Characters(scene, bodies);
    this.bodies = 0; // how many townspeople wear a real body right now
    this.renderer = null; // set by the game (useRenderer) on the legacy renderer, for the cards
    this.cardMesh = null;
    this.cards = null; // the per-instance card attribute once the atlas is baked
    this.frames = null; // and the frame attribute (standing or a walking frame)
  }

  // list: rows of [id, sex, age, role, ax, az, bx, bz, t] from the server.
  // skip: someone not to draw (a passenger riding with the player), or -1.
  setTownspeople(list, skip = -1) {
    const seen = new Set();
    for (const row of list) {
      const [id, sex, age, role, ax, az, bx, bz, t] = row;
      if (id === skip) continue;
      seen.add(id);
      let n = this.npcs.get(id);
      if (!n) {
        n = this.spawn(id, sex, age);
        const k = t >= 0 ? t : hash2(id, 3, 5);
        n.x = ax + (bx - ax) * k;
        n.z = az + (bz - az) * k;
        this.npcs.set(id, n);
      }
      n.role = role;
      n.age = age;
      n.ax = ax;
      n.az = az;
      n.bx = bx;
      n.bz = bz;
      n.walking = t >= 0;
      if (n.walking) {
        n.tx = bx;
        n.tz = bz;
      }
    }
    for (const id of this.npcs.keys()) {
      if (seen.has(id)) continue;
      const gone = this.npcs.get(id);
      if (gone.body) this.characters.giveBack(gone.body);
      this.npcs.delete(id);
    }
  }

  spawn(id, sex, age) {
    const skin = SKIN_TONES[Math.floor(hash2(id, 1, 7) * SKIN_TONES.length)];
    const dress = sex === 0 && age >= 12;
    let top = dress ? DRESS[Math.floor(hash2(id, 2, 7) * DRESS.length)] : SHIRT_COLORS[Math.floor(hash2(id, 2, 7) * SHIRT_COLORS.length)];
    let bottom = TROUSER_COLORS[Math.floor(hash2(id, 3, 7) * TROUSER_COLORS.length)];
    // most school-age children are in uniform
    if (age >= 5 && age < 18 && hash2(id, 8, 7) < 0.7) {
      const u = Math.floor(hash2(id, 9, 7) * UNIFORM_TOPS.length);
      top = UNIFORM_TOPS[u];
      bottom = UNIFORM_BOTTOMS[u];
    }
    const topColour = new THREE.Color(top);
    return { id, sex, age, role: 0, card: cardFor(sex, topColour), skin: new THREE.Color(skin), top: topColour, bottom: new THREE.Color(bottom), dress, x: 0, z: 0, tx: 0, tz: 0, ax: 0, az: 0, bx: 0, bz: 0, walking: false, pause: hash2(id, 4, 7) * 6, phase: hash2(id, 5, 7) * 6, yaw: 0, toB: true, swing: 0, slot: -1, body: null, speed: 0 };
  }

  // rows of [id, name, skin, shirt, trousers, x, y, z, yaw, speed, vehicle]
  // vehicle: 0 on foot, 1 driving a taxi, 2 riding a bike
  setPlayers(list) {
    const seen = new Set();
    for (const row of list) {
      const [id, name, skin, shirt, trousers, x, y, z, yaw, speed, veh = 0] = row;
      if (id === this.me) continue;
      seen.add(id);
      let p = this.players.get(id);
      if (!p) {
        const char = makeCharacter({ skin, shirt, trousers });
        char.group.add(nameTag(name));
        char.group.position.set(x, y, z);
        this.scene.add(char.group);
        p = { char, x, y, z, yaw, speed, veh: 0, car: null, body: null, look: { skin, shirt, trousers } };
        this.players.set(id, p);
      }
      if (p.veh !== veh) {
        if (p.car) this.scene.remove(p.car);
        const type = vehicleTypeOf(veh);
        p.car = type ? vehicleMesh(type) : null;
        if (p.car) this.scene.add(p.car);
        p.veh = veh;
        // a taxi driver is inside the car: only the name shows above it
        p.char.body.visible = !veh || veh === 2; // in a car only the name shows; on a bike the rider
      }
      p.x = x;
      p.y = y;
      p.z = z;
      p.yaw = yaw;
      p.speed = speed;
    }
    for (const [id, p] of this.players) {
      if (seen.has(id)) continue;
      this.scene.remove(p.char.group);
      if (p.car) this.scene.remove(p.car);
      if (p.body) this.characters.giveBack(p.body);
      this.players.delete(id);
    }
  }

  // px, pz: where the player is; townspeople more than FAR metres away are re-posed every third frame.
  update(dt, px = 0, pz = 0) {
    let i = 0;
    this.recoloured = false;
    this.tick = (this.tick || 0) + 1;
    const tick = this.tick;
    for (const n of this.npcs.values()) {
      let speed = 0;
      if (n.pause > 0 && !n.walking) {
        n.pause -= dt;
      } else {
        if (!n.walking) {
          // pottering about the compound or the workplace
          n.tx = n.toB ? n.bx : n.ax;
          n.tz = n.toB ? n.bz : n.az;
        }
        const dx = n.tx - n.x;
        const dz = n.tz - n.z;
        const d = Math.hypot(dx, dz);
        if (d < 0.6) {
          n.toB = !n.toB;
          n.pause = 2 + hash2(n.id, (n.phase * 10) | 0, 9) * 9;
        } else {
          speed = (n.age < 10 ? 1 : 1.35) * (n.walking ? 1 : 0.7);
          n.x += (dx / d) * speed * dt;
          n.z += (dz / d) * speed * dt;
          const fixed = resolveCircle(n.x, n.z, 0.3, this.chunks.collidersNear(n.x, n.z, this.hits));
          n.x = fixed.x;
          n.z = fixed.z;
          // a walker is never allowed through the player: it steps round
          const pdx = n.x - px;
          const pdz = n.z - pz;
          const pd = Math.hypot(pdx, pdz);
          if (pd < 0.75 && pd > 0.001) {
            n.x = px + (pdx / pd) * 0.75;
            n.z = pz + (pdz / pd) * 0.75;
          }
          // the ends of a round are houses and stalls, whose walls stop the walker: at a wall near
          // the goal, or after pressing on one for a while, the walker counts as arrived and turns
          if (fixed.hit) {
            n.stuck = (n.stuck || 0) + dt;
            if (d < 6 || n.stuck > 1.5) {
              n.toB = !n.toB;
              n.pause = 1.5 + hash2(n.id, (n.phase * 10) | 0, 9) * 6;
              n.stuck = 0;
            }
          } else n.stuck = 0;
          const want = Math.atan2(dx, dz);
          let diff = want - n.yaw;
          while (diff > Math.PI) diff -= Math.PI * 2;
          while (diff < -Math.PI) diff += Math.PI * 2;
          n.yaw += diff * Math.min(1, dt * 8);
        }
      }
      n.phase += dt * (speed > 0 ? 6.5 : 0);
      n.swing = speed > 0 ? Math.sin(n.phase) * 0.6 : 0;
      n.speed = speed;
      // close by, a real body walks instead of the box figure
      const d2 = (n.x - px) ** 2 + (n.z - pz) ** 2;
      if (n.body) {
        if (d2 > NEAR_OUT * NEAR_OUT) {
          this.characters.giveBack(n.body);
          n.body = null;
        } else {
          this.characters.pose(n.body, n.x, this.heightmap.heightAt(n.x, n.z) + surfaceLift(this.chunks.surfaceAt(n.x, n.z)), n.z, n.yaw, speed, dt, n.pause > 0 && !n.walking && hash2(n.id, 6, 7) < 0.3);
          if (i < MAX) this.hide(n, i++);
          continue;
        }
      } else if (d2 < NEAR * NEAR && this.characters.ready) {
        n.body = this.characters.borrow(n.sex, hash2(n.id, 1, 7), n.top, n.dress ? n.top : n.bottom);
        if (n.body) {
          this.characters.pose(n.body, n.x, this.heightmap.heightAt(n.x, n.z) + surfaceLift(this.chunks.surfaceAt(n.x, n.z)), n.z, n.yaw, speed, dt);
          if (i < MAX) this.hide(n, i++);
          continue;
        }
      }
      if (i < MAX) {
        // a far walker keeps last frame's pose in its slot unless the slot has changed hands
        const far = (n.x - px) ** 2 + (n.z - pz) ** 2 > FAR * FAR;
        if (!far || n.slot !== i || (tick + n.id) % 3 === 0) this.place(n, i);
        i++;
      }
    }
    if (!this.cards) this.bakeCards();
    if (this.cards) {
      const mesh = this.cardMesh;
      if (i === 0) mesh.setMatrixAt(0, ZERO);
      mesh.count = Math.max(1, i);
      mesh.instanceMatrix.needsUpdate = true;
      if (this.recoloured) this.cards.needsUpdate = true;
      if (this.framesDirty) {
        this.frames.needsUpdate = true;
        this.framesDirty = false;
      }
    } else {
      for (const mesh of Object.values(this.parts)) {
        if (i === 0) mesh.setMatrixAt(0, ZERO);
        mesh.count = Math.max(1, i);
        mesh.instanceMatrix.needsUpdate = true;
        if (this.recoloured && mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
      }
    }
    for (const p of this.players.values()) {
      const g = p.char.group;
      const k = Math.min(1, dt * 10);
      const seat = p.veh === 2 ? VEHICLES.bike.seat : 0;
      g.position.x += (p.x - g.position.x) * k;
      g.position.y += (p.y + seat - g.position.y) * k;
      g.position.z += (p.z - g.position.z) * k;
      g.rotation.y = p.yaw;
      animateCharacter(p.char, p.car ? 0 : p.speed, dt, p.veh === 2);
      if (p.car) {
        p.car.position.set(g.position.x, g.position.y - seat + 0.02, g.position.z);
        p.car.rotation.y = p.yaw;
      }
      // another player wears a real body too, in the look they chose
      if (!p.body && this.characters.ready) {
        const tone = 1 - Math.min(1, parseInt(String(p.look.skin || '#7f5236').replace('#', '').slice(0, 2), 16) / 255);
        p.body = this.characters.borrow(1, tone, p.look.shirt, p.look.trousers);
        if (p.body) p.char.body.visible = false;
      }
      if (p.body) {
        p.body.root.visible = !p.veh || p.veh === 2; // inside a car only the name shows
        p.body.root.position.copy(g.position);
        p.body.root.rotation.copy(g.rotation);
        if (p.veh === 2) this.characters.play(p.body, 'sit', 0.2);
        else this.characters.pose(p.body, g.position.x, g.position.y, g.position.z, g.rotation.y, p.car ? 0 : p.speed, 0);
        p.body.mixer.update(dt);
      }
    }
  }

  // Folds a townsperson's box figure away at slot i (a real body is walking for them).
  hide(n, i) {
    n.slot = i;
    if (this.cards) this.cardMesh.setMatrixAt(i, ZERO);
    else for (const mesh of Object.values(this.parts)) mesh.setMatrixAt(i, ZERO);
  }

  // Gives the far ring its cards: the legacy renderer bakes the atlas once the bodies are in.
  useRenderer(renderer) {
    this.renderer = renderer;
  }

  // An instanced mesh of `slots` seated riders from the same atlas, every slot in a random
  // outfit, for the okadas of the traffic. Null until the atlas is baked.
  riderCards(slots) {
    if (!this.cards) return null;
    const quad = cardQuad(CARD_W, CARD_H, slots);
    for (let i = 0; i < slots; i++) {
      quad.attributes.card.setX(i, Math.floor(hash2(i, 11, 7) * CARDS));
      quad.attributes.frame.setX(i, SEATED_FRAME);
    }
    const mesh = new THREE.InstancedMesh(quad, this.cardMesh.material, slots);
    mesh.count = 1;
    mesh.setMatrixAt(0, ZERO);
    mesh.castShadow = false;
    mesh.frustumCulled = false;
    mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    return mesh;
  }

  bakeCards() {
    if (this.cards || !this.renderer || !this.characters.ready) return;
    const ch = this.characters;
    const r = this.renderer;
    const CW = 128;
    const CH = 256;
    const rt = new THREE.WebGLRenderTarget(CW * CARDS, CH * FRAMES, { format: THREE.RGBAFormat, type: THREE.UnsignedByteType, generateMipmaps: true, minFilter: THREE.LinearMipmapLinearFilter, magFilter: THREE.LinearFilter });
    rt.texture.colorSpace = THREE.LinearSRGBColorSpace;
    const mini = new THREE.Scene();
    const cam = new THREE.OrthographicCamera(-CARD_W / 2, CARD_W / 2, CARD_H, 0, 0.1, 30);
    cam.position.set(0, 0, 10);
    cam.lookAt(0, 0, 0);
    const oldTarget = r.getRenderTarget();
    const oldColor = new THREE.Color();
    r.getClearColor(oldColor);
    const oldAlpha = r.getClearAlpha();
    const size = new THREE.Vector2();
    r.getSize(size);
    r.setRenderTarget(rt);
    r.setClearColor(0x000000, 0);
    r.clear();
    r.setScissorTest(true);
    for (let k = 0; k < CARDS * FRAMES; k++) {
      const column = k % CARDS;
      const row = Math.floor(k / CARDS);
      const sex = column < CARD_MEN.length ? 1 : 0;
      const colour = cardColours[column];
      const body = ch.borrow(sex, 0.3 + column * 0.07, colour, sex === 0 ? colour : '#3a3a4a');
      if (!body) continue;
      // the body's own colours, unlit: the card is lit where it stands
      const swapped = [];
      body.root.traverse((o) => {
        if (!o.isMesh) return;
        const flat = (m) => new THREE.MeshBasicMaterial({ map: m.map || null, color: m.color, side: THREE.DoubleSide, alphaTest: m.alphaTest || 0 });
        swapped.push([o, o.material]);
        o.material = Array.isArray(o.material) ? o.material.map(flat) : flat(o.material);
      });
      // row 0 stands; rows 1 and 2 are the walk a third of a stride apart; row 3 sits (riders)
      if (row === 0) ch.pose(body, 0, 0, 0, 0, 0, 0.4);
      else if (row === SEATED_FRAME) {
        ch.play(body, 'sit', 0);
        body.mixer.update(0.6);
      } else ch.pose(body, 0, 0, 0, 0, 1.3, row === 1 ? 0.25 : 0.65);
      mini.add(body.root);
      body.root.visible = true;
      body.root.updateMatrixWorld(true);
      r.setViewport(column * CW, row * CH, CW, CH);
      r.setScissor(column * CW, row * CH, CW, CH);
      r.render(mini, cam);
      for (const [o, m] of swapped) {
        for (const b of Array.isArray(o.material) ? o.material : [o.material]) b.dispose();
        o.material = m;
      }
      this.scene.add(body.root);
      ch.giveBack(body);
    }
    r.setScissorTest(false);
    r.setRenderTarget(oldTarget);
    r.setViewport(0, 0, size.x, size.y);
    r.setScissor(0, 0, size.x, size.y);
    r.setClearColor(oldColor, oldAlpha);
    const quad = cardQuad(CARD_W, CARD_H, MAX);
    const mesh = new THREE.InstancedMesh(quad, billboardMaterial(rt.texture, { cards: CARDS, rows: FRAMES }), MAX);
    mesh.count = 1;
    mesh.setMatrixAt(0, ZERO);
    mesh.castShadow = false;
    mesh.frustumCulled = false;
    mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    quad.attributes.card.setUsage(THREE.DynamicDrawUsage);
    quad.attributes.frame.setUsage(THREE.DynamicDrawUsage);
    this.frames = quad.attributes.frame;
    this.scene.add(mesh);
    this.cardMesh = mesh;
    this.cards = quad.attributes.card;
    for (const part of Object.values(this.parts)) part.visible = false;
  }

  // Writes one townsperson's eleven part matrices at instance slot i, and the colours too when the
  // person has moved to a different slot since last frame.
  place(n, i) {
    const recolour = n.slot !== i;
    if (recolour) {
      n.slot = i;
      this.recoloured = true;
    }
    const root = this.root;
    root.position.set(n.x, this.heightmap.heightAt(n.x, n.z) + surfaceLift(this.chunks.surfaceAt(n.x, n.z)), n.z);
    root.rotation.set(0, n.yaw, 0);
    root.scale.setScalar(sizeFor(n.age));
    root.updateMatrixWorld(true);
    if (this.cards) {
      // a card: its column is the person's outfit, its scale the person's size
      this.cardMesh.setMatrixAt(i, root.matrixWorld);
      if (recolour) this.cards.setX(i, n.card);
      // walking: the two frames alternate with the stride; standing: the first row
      const frame = n.speed > 0.15 ? 1 + (Math.floor(n.phase / Math.PI) % 2) : 0;
      if (this.frames.getX(i) !== frame) {
        this.frames.setX(i, frame);
        this.framesDirty = true;
      }
      return;
    }
    const limb = this.limb;
    const parts = this.parts;
    const put = (name, colour, pivot, swingX) => {
      const mesh = parts[name];
      if (pivot) {
        limb.position.set(pivot[0], pivot[1], pivot[2]);
        limb.rotation.set(swingX, 0, 0);
        limb.updateMatrixWorld(true);
        mesh.setMatrixAt(i, limb.matrixWorld);
      } else mesh.setMatrixAt(i, root.matrixWorld);
      if (recolour) mesh.setColorAt(i, colour);
    };
    put('head', n.skin);
    put('hair', HAIR);
    put('torso', n.top);
    // a dress hides the trousers; without one the skirt is folded away to nothing
    put('hips', n.dress ? n.top : n.bottom);
    put('skirt', n.top);
    if (n.dress) parts.hips.setMatrixAt(i, ZERO);
    else parts.skirt.setMatrixAt(i, ZERO);
    const legs = n.dress ? n.skin : n.bottom;
    put('armL', n.top, PARTS.armL.pivot, -n.swing * 0.8);
    put('armR', n.top, PARTS.armR.pivot, n.swing * 0.8);
    put('legL', legs, PARTS.legL.pivot, n.swing);
    put('legR', legs, PARTS.legR.pivot, -n.swing);
    put('shoeL', SHOE, PARTS.shoeL.pivot, n.swing);
    put('shoeR', SHOE, PARTS.shoeR.pivot, -n.swing);
  }

  // The townsperson closest to a point, within reach metres, or null.
  nearest(x, z, reach) {
    let best = null;
    let bestD = reach * reach;
    for (const n of this.npcs.values()) {
      const d = (n.x - x) ** 2 + (n.z - z) ** 2;
      if (d < bestD) {
        bestD = d;
        best = n;
      }
    }
    return best;
  }
}

function nameTag(name) {
  const c = document.createElement('canvas');
  const g = c.getContext('2d');
  const font = '600 30px "Barlow Semi Condensed", Arial, sans-serif';
  g.font = font;
  c.width = Math.ceil(g.measureText(name).width) + 20;
  c.height = 42;
  g.fillStyle = 'rgba(27,30,35,0.8)';
  g.fillRect(0, 0, c.width, c.height);
  g.font = font;
  g.textBaseline = 'middle';
  g.fillStyle = '#f2b705';
  g.fillText(name, 10, 22);
  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  const s = new THREE.Sprite(new THREE.SpriteMaterial({ map: tex, fog: false }));
  s.scale.set((c.width / 42) * 0.34, 0.34, 1);
  s.position.set(0, 2.15, 0);
  return s;
}
